/*
 * Verzend- en valgarantie (okt 2026). Eén vaste regel per bestelling, qty 1.
 *
 * <val-garantie>      winkelwagen (lade + wagenpagina): vinkje aan/uit + zelfherstel.
 * <val-garantie-pdp>  onder de koopknop: aangevinkt = garantie mee in DEZELFDE /cart/add.js-call
 *                     als het hoofdproduct (items-array). Geen tweede, losse request.
 *
 * Wagen aan/uit gaat via /cart/update.js met updates {variant: 1|0}: zet het aantal exact
 * (dubbelklik kan nooit qty 2 geven), en geeft net als gift-wrapping.js de hele wagen plus de
 * gebundelde secties terug, zodat de lade/wagenpagina op de bestaande manier opnieuw rendert
 * (pubsub cartUpdate -> CartItems.onCartUpdate in cart.js).
 */
(() => {
  if (window.numsyValGarantie) return;

  const HEAL_GUARD_MS = 5000;
  let lastHeal = { sig: '', at: 0 };

  class ValGarantie extends HTMLElement {
    connectedCallback() {
      this.variantId = parseInt(this.dataset.variantId, 10);
      this.qty = parseInt(this.dataset.qty, 10) || 0;
      this.hasElec = this.dataset.hasElec === 'true';
      if (!this.variantId) return;

      this.checkbox = this.querySelector('.val-garantie__check');
      if (this.checkbox) {
        this.checkbox.addEventListener('change', () => this.setQuantity(this.checkbox.checked ? 1 : 0));
      }
      this.heal();
    }

    // Garantie zonder elektronica eruit; meer dan 1 terug naar 1.
    heal() {
      let target = null;
      if (!this.hasElec && this.qty > 0) target = 0;
      else if (this.qty > 1) target = 1;
      if (target === null) return;

      // Lusbeveiliging: dezelfde reparatie niet binnen 5 s opnieuw (bv. als de update faalt).
      const sig = `${this.variantId}:${this.qty}:${this.hasElec}>${target}`;
      if (lastHeal.sig === sig && Date.now() - lastHeal.at < HEAL_GUARD_MS) return;
      lastHeal = { sig, at: Date.now() };
      this.setQuantity(target);
    }

    setQuantity(quantity) {
      if (this.busy) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      const loader = this.querySelector('.val-garantie__loader');
      if (loader) loader.hidden = false;
      if (this.checkbox) this.checkbox.disabled = true;

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
      const body = JSON.stringify({ updates: { [this.variantId]: quantity }, sections });

      fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((state) => {
          if (state.status || !state.items) throw new Error(state.description || state.message || 'cart update failed');
          // Rendert de lade en/of wagenpagina opnieuw; dit element wordt daarbij vervangen.
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'val-garantie', cart: state });
        })
        .catch((error) => {
          console.error('val-garantie', error);
          if (this.checkbox) this.checkbox.checked = this.qty > 0;
        })
        .finally(() => {
          this.busy = false;
          this.removeAttribute('aria-busy');
          if (loader) loader.hidden = true;
          if (this.checkbox) this.checkbox.disabled = false;
        });
    }
  }
  if (!customElements.get('val-garantie')) customElements.define('val-garantie', ValGarantie);

  class ValGarantiePdp extends HTMLElement {
    /* "Nu kopen" slaat de wagen over en neemt de garantie niet mee: verbergen zolang het vinkje aan staat. */
    connectedCallback() {
      const cb = this.querySelector('.val-garantie__check');
      if (!cb) return;
      const sync = () => {
        const form = this.dataset.formId && document.getElementById(this.dataset.formId);
        const scope = this.closest('.buy-buttons') || (form && form.parentElement) || document;
        scope.querySelectorAll('.shopify-payment-button').forEach((el) => { el.style.display = cb.checked ? 'none' : ''; });
      };
      cb.addEventListener('change', sync);
      sync();
    }

    get variantId() { return parseInt(this.dataset.variantId, 10); }
    get checked() {
      const cb = this.querySelector('.val-garantie__check');
      return !!(cb && cb.checked && !cb.disabled);
    }
  }
  if (!customElements.get('val-garantie-pdp')) customElements.define('val-garantie-pdp', ValGarantiePdp);

  const pdpFor = (form) => {
    const id = form && form.getAttribute && form.getAttribute('id');
    if (!id) return null;
    return document.querySelector(`val-garantie-pdp[data-form-id="${CSS.escape(id)}"]`);
  };

  /*
   * Standaard product-form (assets/theme.js ProductForm.onSubmitHandler) bouwt `new FormData(this)`
   * en post die naar /cart/add.js. Het bouwen van die FormData vuurt synchroon een `formdata`-event
   * op het formulier; daarin herschrijven we de data naar items[0] = garantie, items[1] = hoofdproduct
   * (laatst toegevoegd staat bovenaan in de wagen). `id` moet eruit: Shopify verwerkt `id` en `items`
   * allebei, dan komt het hoofdproduct er dubbel in (getest 22 okt 2026 op numsy.nl).
   */
  const rewrite = (fd, vgId) => {
    const mainId = fd.get('id');
    if (!mainId) return false;
    const qty = fd.get('quantity') || '1';
    const sellingPlan = fd.get('selling_plan');
    const props = [];
    for (const [key, value] of fd.entries()) {
      const m = /^properties\[(.+)\]$/.exec(key);
      if (m) props.push([m[1], value]);
    }
    [...new Set([...fd.keys()])].forEach((key) => {
      if (key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties[')) fd.delete(key);
    });
    fd.append('items[0][id]', String(vgId));
    fd.append('items[0][quantity]', '1');
    fd.append('items[1][id]', mainId);
    fd.append('items[1][quantity]', qty);
    if (sellingPlan) fd.append('items[1][selling_plan]', sellingPlan);
    props.forEach(([k, v]) => fd.append(`items[1][properties][${k}]`, v));
    return true;
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const pdp = pdpFor(form);
    if (!pdp || !pdp.checked) return;

    const onFormData = (e) => { rewrite(e.formData, pdp.variantId); };
    form.addEventListener('formdata', onFormData, { once: true });
    // Alleen voor deze ene submit; vuurt er geen FormData (bv. bundle-builder nam het over), dan weer weg.
    setTimeout(() => form.removeEventListener('formdata', onFormData), 0);
  }, true);

  // Voor snippets/numsy-bundle-builder.liquid (eigen JSON-post met items-array).
  window.numsyValGarantie = {
    itemFor(form) {
      const pdp = pdpFor(form);
      if (!pdp || !pdp.checked) return null;
      return { id: pdp.variantId, quantity: 1 };
    }
  };
})();
